import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Icon from "@mui/material/Icon";
import DataTable from "examples/Tables/DataTable";
import useApi from "components/Utils/api";
import toast from "react-hot-toast";
import RouteAccountDialog from "../dialogs/RouteAccountDialog";
import PermissionControl from "components/Utils/PermissionControl";

// ── Cell renderers ───────────────────────────────────────────────────────────

function DefaultCell({ cell }) {
  return cell.value ? <Chip label="Default" size="small" color="primary" /> : null;
}
DefaultCell.propTypes = { cell: PropTypes.shape({ value: PropTypes.bool }) };

function StatusCell({ cell }) {
  return <Chip label={cell.value} size="small" color={cell.value === "ACTIVE" ? "success" : "default"} />;
}
StatusCell.propTypes = { cell: PropTypes.shape({ value: PropTypes.string }) };

function EditCell({ row, onEdit }) {
  return (
    <PermissionControl permission={{ module: "SettlementRoutes", subModule: "Update" }}>
      <Tooltip title="Edit Account" arrow>
        <div>
          <RemoveRedEyeIcon
            onClick={() => onEdit(row.original)}
            style={{ cursor: "pointer", fontSize: 20 }}
          />
        </div>
      </Tooltip>
    </PermissionControl>
  );
}
EditCell.propTypes = {
  row: PropTypes.shape({ original: PropTypes.object }),
  onEdit: PropTypes.func.isRequired,
};

// ── Tab ──────────────────────────────────────────────────────────────────────

RouteAccountsTab.propTypes = {
  routeId: PropTypes.string.isRequired,
};

export default function RouteAccountsTab({ routeId }) {
  const api = useApi();
  const [rows, setRows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get(`settlement-routes/${routeId}/accounts`)
      .then((res) => setRows(res.data || []))
      .catch(() => toast.error("Failed to load accounts"));
  };

  useEffect(() => { load(); }, [routeId]);

  const handleSave = async (data) => {
    try {
      if (editing) {
        await api.put(`settlement-routes/${routeId}/accounts/${editing.id}`, data);
        toast.success("Account updated");
      } else {
        await api.post(`settlement-routes/${routeId}/accounts`, data);
        toast.success("Account added");
      }
      setDialogOpen(false);
      setEditing(null);
      load();
    } catch {
      toast.error("Failed to save account");
    }
  };

  const handleEdit = (row) => { setEditing(row); setDialogOpen(true); };

  const columns = [
    { Header: "Currency",       accessor: "currency",      width: "10%", align: "left" },
    { Header: "Account Number", accessor: "accountNumber", width: "20%", align: "left" },
    { Header: "Account Name",   accessor: "accountName",   width: "20%", align: "left" },
    { Header: "Swift Code",     accessor: "swiftCode",     width: "12%", align: "left" },
    { Header: "Default", accessor: "isDefault", width: "10%", align: "center", Cell: DefaultCell },
    { Header: "Status",  accessor: "status",    width: "10%", align: "left",   Cell: StatusCell },
    {
      Header: "Actions",
      accessor: "actions",
      width: "18%",
      align: "left",
      // eslint-disable-next-line react/prop-types
      Cell: ({ row }) => <EditCell row={row} onEdit={handleEdit} />,
    },
  ];

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MDTypography variant="h6">Currency Accounts</MDTypography>
        <PermissionControl permission={{ module: "SettlementRoutes", subModule: "Create" }}>
          <MDButton variant="gradient" color="info" size="small" startIcon={<Icon>add</Icon>} onClick={() => { setEditing(null); setDialogOpen(true); }}>
            Add Account
          </MDButton>
        </PermissionControl>
      </MDBox>
      <DataTable table={{ columns, rows }} showTotalEntries={false} isSorted={false} noEndBorder entriesPerPage={false} />
      <RouteAccountDialog open={dialogOpen} onClose={() => { setDialogOpen(false); setEditing(null); }} onSave={handleSave} initial={editing} />
    </MDBox>
  );
}
